import React, { useEffect, useRef, useState } from 'react';
import { Form, Modal, Button } from 'react-bootstrap';

/**
 * Compact side editor: text + optional image URL → HTML (frontHtml / backHtml).
 */
export default function FlashcardSideEditor({ value, onChange, placeholder }) {
  const editorRef = useRef(null);
  const rootRef = useRef(null);
  const [imageOpen, setImageOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const next = value || '';
    if (el.innerHTML !== next) {
      el.innerHTML = next;
    }
  }, [value]);

  const emit = () => {
    const html = editorRef.current?.innerHTML || '';
    onChange?.(html);
    // Nudge parent row height sync after content changes
    const root = rootRef.current;
    if (root) {
      root.dispatchEvent(new Event('fc-side-resize', { bubbles: true }));
    }
  };

  const insertImage = (e) => {
    e.preventDefault();
    const url = imageUrl.trim();
    if (!url) return;
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const img = document.createElement('img');
    img.src = url;
    img.alt = imageAlt.trim() || '';
    img.style.maxWidth = '100%';
    img.style.height = 'auto';
    const sel = window.getSelection();
    if (sel && sel.rangeCount && el.contains(sel.anchorNode)) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(img);
      range.setStartAfter(img);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      el.appendChild(document.createElement('br'));
      el.appendChild(img);
    }
    emit();
    setImageOpen(false);
    setImageUrl('');
    setImageAlt('');
  };

  return (
    <div className="fc-side-editor" ref={rootRef}>
      <div
        ref={editorRef}
        className="fc-side-editor-body eval-rich-content"
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder || 'Enter text…'}
        onInput={emit}
        onBlur={emit}
      />
      <button
        type="button"
        className="fc-side-editor-image-btn"
        title="Insert image"
        aria-label="Insert image"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setImageOpen(true)}
      >
        <i className="bi bi-image" aria-hidden />
      </button>
      <Modal show={imageOpen} onHide={() => setImageOpen(false)} centered>
        <Form onSubmit={insertImage}>
          <Modal.Header closeButton>
            <Modal.Title>Insert image</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Image URL</Form.Label>
              <Form.Control
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://…"
                autoFocus
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Alt text (optional)</Form.Label>
              <Form.Control
                value={imageAlt}
                onChange={(e) => setImageAlt(e.target.value)}
                placeholder="Description"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setImageOpen(false)}>Cancel</Button>
            <Button type="submit" variant="primary">Insert</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
